import { Box, Typography } from "@material-ui/core";

type Props = {
  /** The template body, with `{{listing_link}}` where the link goes. */
  body: string;
  /** The link to show in place of `{{listing_link}}`. */
  link: string;
  footer?: string;
  headerImageUrl?: string;
};

const LINK_PLACEHOLDER = "{{listing_link}}";

/**
 * The WhatsApp-style bubble the listing-share dialog previews — the message exactly as a
 * recipient will see it.
 *
 * The link is rendered as an ISOLATED left-to-right run. Inside a right-to-left sentence a bare
 * URL is reordered by the bidi algorithm and reads backwards (".../app.webot.co.il/myListings"),
 * which is how the first preview made a correct per-listing link look like the general minisite
 * link.
 */
export default function ListingSharePreviewBubble({ body, link, footer, headerImageUrl }: Props): JSX.Element {
  const at = body.indexOf(LINK_PLACEHOLDER);
  const before = at === -1 ? body : body.slice(0, at);
  const after = at === -1 ? "" : body.slice(at + LINK_PLACEHOLDER.length);

  return (
    <Box sx={{ maxWidth: 320, bgcolor: "common.white", borderRadius: 1.5, overflow: "hidden", boxShadow: 1, color: "grey.800" }}>
      {headerImageUrl && (
        <Box component="img" src={headerImageUrl} alt="" sx={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} />
      )}
      <Box sx={{ p: 1.25 }}>
        <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {before}
          {at !== -1 && (
            <Box component="bdi" dir="ltr" sx={{ color: "info.main", wordBreak: "break-all", unicodeBidi: "isolate" }}>
              {link}
            </Box>
          )}
          {after}
        </Typography>
        {footer && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
            {footer}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
